import { createContext, useContext, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  setTheme as setThemeAction,
  toggleTheme as toggleThemeAction,
  setSystemTheme as setSystemThemeAction,
} from 'redux_store';
import './tokens.css';

/**
 * QHS Theme Provider.
 *
 * Single source of truth for the visual theme is the Redux store
 * (`state.appSettings`): `themePreference` is what the user chose
 * ('light' | 'dark' | 'auto'), `theme` is the effective value that
 * reaches the DOM. This provider:
 *
 *   1. Reflects the effective theme to `<html data-theme>`, which is
 *      what tokens.css hangs its selectors off of.
 *   2. Keeps `auto` live: listens to prefers-color-scheme and pushes
 *      OS switches into the store, so a System user sees the change
 *      without a reload.
 *   3. Exposes `{ theme, preference, setTheme, toggleTheme }` so
 *      components can call `useTheme()` without knowing about redux.
 *
 * `setTheme` here is LOCAL only. Persisting to the backend is the
 * user menu's job (saveAppearance thunk), which applies optimistically
 * through the same store.
 */

const ThemeContext = createContext({
  theme: 'light',
  preference: 'light',
  setTheme: () => {},
  toggleTheme: () => {},
});

const DARK_SCHEME_QUERY = '(prefers-color-scheme: dark)';

export const ThemeProvider = ({ children }) => {
  const theme = useSelector(state => state.appSettings.theme);
  const preference = useSelector(state => state.appSettings.themePreference);
  const dispatch = useDispatch();

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
  }, [theme]);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const media = window.matchMedia(DARK_SCHEME_QUERY);
    const sync = () => dispatch(setSystemThemeAction(media.matches ? 'dark' : 'light'));
    // The initial value was resolved when the store was created; the
    // OS may have flipped between then and mount, so sync once.
    sync();
    // Older Safari only has the deprecated addListener pair.
    if (media.addEventListener) {
      media.addEventListener('change', sync);
      return () => media.removeEventListener('change', sync);
    }
    media.addListener(sync);
    return () => media.removeListener(sync);
  }, [dispatch]);

  const setTheme = useCallback(next => dispatch(setThemeAction(next)), [dispatch]);
  const toggleTheme = useCallback(() => dispatch(toggleThemeAction()), [dispatch]);

  return (
    <ThemeContext.Provider value={{ theme, preference, setTheme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => useContext(ThemeContext);
